import React, { useState, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Grid,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FolderIcon from "@mui/icons-material/Folder";
import CloseIcon from "@mui/icons-material/Close";
import api, { getCurrentMode } from "../../utils/api";
import RecruitFolders from "./components/RecruitFolders";
import AthleteQuickView from "../prospects/profile/components/AthleteQuickView";

// No longer needed since we removed the status column

const ProspectsList = () => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({
    status: "",
    position: "",
    graduationYear: "",
  });
  const [activeFolder, setActiveFolder] = useState({});
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Quick view state
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewPosition, setQuickViewPosition] = useState({
    top: 0,
    left: 0,
  });
  const [quickViewAthlete, setQuickViewAthlete] = useState(null);
  const quickViewTimeoutRef = useRef(null);

  // Theme and responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Fetch prospects using the API utility
  useEffect(() => {
    const fetchProspects = async () => {
      try {
        setLoading(true);
        setError(null);

        const prospectsData = await api.prospects.getAll();
        setProspects(prospectsData);
      } catch (err) {
        console.error("Error fetching prospects:", err);
        setError(err.message || "Failed to fetch prospects");
      } finally {
        setLoading(false);
      }
    };

    fetchProspects();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Scroll to top of the page when changing pages on mobile
    if (isMobile) {
      // Use requestAnimationFrame to ensure DOM updates are complete
      requestAnimationFrame(() => {
        // Scroll to the very top of the page
        window.scrollTo({ top: 0, behavior: "smooth" });

        // Also try scrolling the main container to ensure we're at the top
        const container = document.querySelector(
          '[data-testid="prospects-container"]'
        );
        if (container) {
          container.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleFilterChange = (e) => {
    setFilter({
      ...filter,
      [e.target.name]: e.target.value,
    });
    setPage(0);
  };

  const handleFolderSelect = (folderFilter) => {
    // Update filter based on folder selection
    setFilter({
      ...filter,
      position: folderFilter.position || "",
      graduationYear: folderFilter.graduationYear || "",
    });
    setActiveFolder(folderFilter);
    setPage(0);

    // Close mobile drawer after folder selection
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Handle quick view hover events
  const handleQuickViewEnter = (event, prospect) => {
    if (quickViewTimeoutRef.current) {
      clearTimeout(quickViewTimeoutRef.current);
    }

    // Store the current target element since it might not be available in the timeout
    const currentTarget = event.currentTarget;

    quickViewTimeoutRef.current = setTimeout(() => {
      // Check if the element still exists in the DOM
      if (currentTarget && document.body.contains(currentTarget)) {
        const rect = currentTarget.getBoundingClientRect();

        // Calculate position ensuring the quick view doesn't go off-screen
        const windowWidth = window.innerWidth;
        const quickViewWidth = 320; // Width of the quick view component

        let left = rect.left + window.scrollX;
        // If the quick view would go off the right edge, align it to the right edge of the row
        if (left + quickViewWidth > windowWidth - 20) {
          left = Math.max(20, rect.right + window.scrollX - quickViewWidth);
        }

        const position = {
          top: rect.bottom + window.scrollY + 5, // Add a small gap
          left: left,
        };

        setQuickViewPosition(position);
        setQuickViewAthlete(prospect);
        setQuickViewOpen(true);
      }
    }, 300); // Show after 300ms hover
  };

  const handleQuickViewLeave = () => {
    if (quickViewTimeoutRef.current) {
      clearTimeout(quickViewTimeoutRef.current);
    }

    quickViewTimeoutRef.current = setTimeout(() => {
      setQuickViewOpen(false);
    }, 200); // Hide after 200ms
  };

  // Filter prospects based on search and filters
  const filteredProspects = prospects.filter((prospect) => {
    const matchesSearch =
      search === "" ||
      (prospect.firstName &&
        prospect.firstName.toLowerCase().includes(search.toLowerCase())) ||
      (prospect.lastName &&
        prospect.lastName.toLowerCase().includes(search.toLowerCase())) ||
      (prospect.highSchool &&
        prospect.highSchool.toLowerCase().includes(search.toLowerCase())) ||
      (prospect.position &&
        prospect.position.toLowerCase().includes(search.toLowerCase()));

    const matchesStatus =
      filter.status === "" || prospect.recruitingStatus === filter.status;
    const matchesPosition =
      filter.position === "" || prospect.position === filter.position;
    const matchesGraduationYear =
      filter.graduationYear === "" ||
      (prospect.graduationYear &&
        prospect.graduationYear.toString() === filter.graduationYear);

    return (
      matchesSearch && matchesStatus && matchesPosition && matchesGraduationYear
    );
  });

  // Get unique values for filters - filter out undefined/null values
  const positions = [
    ...new Set(prospects.map((p) => p.position).filter(Boolean)),
  ];
  const graduationYears = [
    ...new Set(prospects.map((p) => p.graduationYear).filter(Boolean)),
  ].sort();
  const statuses = [
    "prospect",
    "contacted",
    "interested",
    "offered",
    "committed",
    "signed",
    "enrolled",
    "inactive",
  ];

  // Paginate the filtered prospects
  const paginatedProspects = filteredProspects.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" data-testid="prospects-container">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Prospects
            {activeFolder.position && ` - ${activeFolder.position}s`}
            {activeFolder.graduationYear && ` (${activeFolder.graduationYear})`}
          </Typography>

          {/* Mobile action buttons container */}
          {isMobile && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mt: 2,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FolderIcon />}
                onClick={toggleMobileDrawer}
                size="small"
              >
                Folders
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileUploadIcon />}
                component={RouterLink}
                to="/prospects/import"
                size="small"
              >
                Import
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                component={RouterLink}
                to="/prospects/add"
                size="small"
              >
                Add Prospect
              </Button>
            </Box>
          )}
        </Box>

        {/* Desktop action buttons */}
        {!isMobile && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileUploadIcon />}
              component={RouterLink}
              to="/prospects/import"
            >
              Import
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              component={RouterLink}
              to="/prospects/add"
            >
              Add Prospect
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Mobile Drawer for Recruit Folders */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Recruit Folders</Typography>
          <IconButton onClick={() => setMobileDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <RecruitFolders
          prospects={prospects}
          onFolderSelect={handleFolderSelect}
        />
      </Drawer>

      <Grid container spacing={3}>
        {/* Desktop Recruit Folders */}
        <Grid item xs={12} md={3} sx={{ display: { xs: "none", md: "block" } }}>
          <RecruitFolders
            prospects={prospects}
            onFolderSelect={handleFolderSelect}
          />
        </Grid>

        {/* Main content - full width on mobile, 9 columns on desktop */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ mb: 3, p: 2 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={search}
                onChange={handleSearchChange}
                sx={{ flexGrow: 1, minWidth: "200px" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: "150px" }}>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status-filter"
                  name="status"
                  value={filter.status}
                  label="Status"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: "150px" }}>
                <InputLabel id="position-filter-label">Position</InputLabel>
                <Select
                  labelId="position-filter-label"
                  id="position-filter"
                  name="position"
                  value={filter.position}
                  label="Position"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All Positions</MenuItem>
                  {positions.map((position) => (
                    <MenuItem key={position} value={position}>
                      {position}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: "120px" }}>
                <InputLabel id="year-filter-label">Grad Year</InputLabel>
                <Select
                  labelId="year-filter-label"
                  id="year-filter"
                  name="graduationYear"
                  value={filter.graduationYear}
                  label="Grad Year"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">All Years</MenuItem>
                  {graduationYears.map((year) => (
                    <MenuItem key={year} value={year.toString()}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>

          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>High School</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Height</TableCell>
                    <TableCell>Weight</TableCell>
                    <TableCell>Grad Year</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedProspects.map((prospect) => (
                    <TableRow
                      key={prospect._id || prospect.id}
                      hover
                      onMouseEnter={(e) => handleQuickViewEnter(e, prospect)}
                      onMouseLeave={handleQuickViewLeave}
                      sx={{
                        bgcolor: (theme) => {
                          const status =
                            prospect.recruitingStatus || "prospect";
                          switch (status) {
                            case "committed":
                            case "signed":
                            case "enrolled":
                              return theme.palette.success.light + "30"; // High interest - light green with 30% opacity
                            case "offered":
                            case "interested":
                              return theme.palette.warning.light + "30"; // Medium interest - light orange with 30% opacity
                            case "contacted":
                              return theme.palette.info.light + "20"; // Low interest - light blue with 20% opacity
                            case "inactive":
                              return theme.palette.error.light + "15"; // Inactive - light red with 15% opacity
                            default:
                              return "transparent"; // Default - no background color
                          }
                        },
                        cursor: "pointer",
                      }}
                    >
                      <TableCell>
                        <Typography
                          component={RouterLink}
                          to={`/prospects/${prospect._id || prospect.id}`}
                          sx={{
                            textDecoration: "none",
                            color: "primary.main",
                            fontWeight: "medium",
                            "&:hover": { textDecoration: "underline" },
                            cursor: "pointer",
                          }}
                        >
                          {prospect.firstName} {prospect.lastName}
                        </Typography>
                      </TableCell>
                      <TableCell>{prospect.position || "N/A"}</TableCell>
                      <TableCell>{prospect.highSchool || "N/A"}</TableCell>
                      <TableCell>
                        {prospect.address?.state || prospect.state || "N/A"}
                      </TableCell>
                      <TableCell>{prospect.height || "N/A"}</TableCell>
                      <TableCell>
                        {prospect.weight || "N/A"}{" "}
                        {prospect.weight ? "lbs" : ""}
                      </TableCell>
                      <TableCell>{prospect.graduationYear || "N/A"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredProspects.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Quick view hover card */}
      <Box
        sx={{
          position: "fixed",
          top: quickViewPosition.top,
          left: quickViewPosition.left,
          zIndex: 1500,
        }}
        onMouseEnter={() => {
          if (quickViewTimeoutRef.current) {
            clearTimeout(quickViewTimeoutRef.current);
          }
          setQuickViewOpen(true);
        }}
        onMouseLeave={handleQuickViewLeave}
      >
        <AthleteQuickView athlete={quickViewAthlete} open={quickViewOpen} />
      </Box>
    </Container>
  );
};

export default ProspectsList;
